import { useFormContext, useWatch } from 'react-hook-form';
import {
  DashboardPageFormBody,
  DashboardPageFormField,
  DashboardPageFormToggleSection,
} from '@shigo-ui/dashboard-ui';
import { Input } from '@shigo-ui/ui';

import type { DemandPartnerFormValue } from '../../formSchema';
import { DemandPartnerNumberField } from './DemandPartnerNumberField';
import { FormSwitchField } from './FormSwitchField';

export function DemandPartnerDeliveryCapFields() {
  const { control, setValue } = useFormContext<DemandPartnerFormValue>();
  const enabled = useWatch({ control, name: 'revenueCapEnabled' });
  return (
    <DashboardPageFormBody
      title="Delivery cap"
      description="Limit the partner's daily revenue exposure."
    >
      <DashboardPageFormToggleSection
        checked={enabled}
        label="Daily revenue cap"
        description="The accumulated amount resets at midnight each day."
        onCheckedChange={(checked) => {
          setValue('revenueCapEnabled', checked, { shouldDirty: true });
          if (!checked) setValue('revenueCap', undefined, { shouldDirty: true });
        }}
      >
        <DemandPartnerNumberField
          name="revenueCap"
          label="Revenue cap"
          required
          hint="Non-negative daily limit in the network currency."
          prefix="$"
          min={0}
          step={0.01}
        />
      </DashboardPageFormToggleSection>
    </DashboardPageFormBody>
  );
}

export function DemandPartnerUserSyncFields() {
  const { register, control, setValue, formState } = useFormContext<DemandPartnerFormValue>();
  const redirectEnabled = useWatch({ control, name: 'userSyncRedirectEnabled' });
  const iframeEnabled = useWatch({ control, name: 'userSyncIframeEnabled' });
  return (
    <DashboardPageFormBody
      title="User sync"
      description="Configure cache identity and bidder user-sync endpoints. Disabled endpoints are saved as null."
    >
      <FormSwitchField
        name="includeUserIdInCacheKey"
        label="User ID cache key"
        hint="Include the resolved user identifier when caching bidder responses."
      />
      <DashboardPageFormToggleSection
        checked={redirectEnabled}
        label="Redirect user sync"
        description="Enable a redirect-based sync endpoint."
        onCheckedChange={(checked) => {
          setValue('userSyncRedirectEnabled', checked, { shouldDirty: true });
          if (!checked) setValue('userSyncRedirect', '', { shouldDirty: true });
        }}
      >
        <DashboardPageFormField
          label="Redirect URL"
          required
          hint="Absolute bidder redirect user-sync URL."
          error={formState.errors.userSyncRedirect?.message}
        >
          <Input type="url" {...register('userSyncRedirect')} />
        </DashboardPageFormField>
      </DashboardPageFormToggleSection>
      <DashboardPageFormToggleSection
        checked={iframeEnabled}
        label="Iframe user sync"
        description="Enable an iframe-based sync endpoint."
        onCheckedChange={(checked) => {
          setValue('userSyncIframeEnabled', checked, { shouldDirty: true });
          if (!checked) setValue('userSyncIframe', '', { shouldDirty: true });
        }}
      >
        <DashboardPageFormField
          label="Iframe URL"
          required
          hint="Absolute bidder iframe user-sync URL."
          error={formState.errors.userSyncIframe?.message}
        >
          <Input type="url" {...register('userSyncIframe')} />
        </DashboardPageFormField>
      </DashboardPageFormToggleSection>
    </DashboardPageFormBody>
  );
}
